"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { clearScheduleOverride, getGetScheduleQueryKey, getSchedule, setScheduleOverride } from "@/lib/api/generated";
import type { ClearScheduleOverrideParams, GetScheduleParams, OverrideClearedOut, OverrideIn } from "@/lib/api/model";

export const SCHEDULE_WINDOW_DAYS = 28;
/** `GET /schedule` max (§10.75). The route defaults to 200; we always ask for the ceiling. */
export const SCHEDULE_LIMIT = 500;
/** Every schedule query key starts with the route path (Orval: `[`/api/conversations/schedule`, params]`); invalidating the prefix refreshes every window. */
export const SCHEDULE_QUERY_PREFIX = "/api/conversations/schedule";

/** Local midnight today → +28 days: Recall's own forward window (spec 04 §7.2), so the tab never asks for events sync cannot know. The server also clamps to 28 days (§10.75). */
export function scheduleWindow(now: Date = new Date()): GetScheduleParams {
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  const to = new Date(from.getTime() + SCHEDULE_WINDOW_DAYS * 86_400_000);
  return { from: from.toISOString(), to: to.toISOString(), limit: SCHEDULE_LIMIT };
}

/** The route pages at `limit` and does not say whether more rows exist — a full page *may* be truncated. */
export function isScheduleTruncated(itemCount: number, limit: number | undefined): boolean {
  return typeof limit === "number" && itemCount >= limit;
}

export function useSchedule(params: GetScheduleParams, enabled = true) {
  return useQuery({ queryKey: getGetScheduleQueryKey(params), queryFn: () => getSchedule(params), enabled, staleTime: 30_000 });
}

export interface OverrideArgs { calendarEventId: string; body: OverrideIn }

/** `POST /conversations/schedule/{calendar_event_id}/override` (spec 04 §7.4). The server re-evaluates the decision; refetch the window. */
export function useScheduleOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ calendarEventId, body }: OverrideArgs) => setScheduleOverride(calendarEventId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [SCHEDULE_QUERY_PREFIX] }),   // prefix: every window
  });
}

export interface ClearOverrideArgs { calendarEventId: string; scope: ClearScheduleOverrideParams["scope"] }   // required — 04d's DELETE declares `scope … = Query(...)`; `OverrideIn.scope` is optional (defaults to "occurrence"), so it cannot type this

/** `DELETE /conversations/schedule/{calendar_event_id}/override?scope=` (04d Task 7 `clear_schedule_override`): drops the preference row and re-evaluates. */
export function useScheduleOverrideClear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ calendarEventId, scope }: ClearOverrideArgs): Promise<OverrideClearedOut> => clearScheduleOverride(calendarEventId, { scope }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [SCHEDULE_QUERY_PREFIX] }),
  });
}
